import { useState, useEffect } from 'react';
import API, { fmt } from '../utils/api';
import Pagination from '../components/Pagination';

const ENTITY_ICONS = {
  order: '🛒', customer: '👥', payment: '💰',
  inventory: '📦', production: '🏭', user: '👤', system: '⚙️'
};

const ACTION_COLOR = {
  'إنشاء': 'badge-success', 'تعديل': 'badge-warning', 'حذف': 'badge-danger',
  'تغيير حالة': 'badge-primary', 'تغيير حالة الإنتاج': 'badge-primary',
  'تعديل كمية منتجة': 'badge-warning', 'دفعة': 'badge-info',
};

function getActionColor(action) {
  for (const [key, val] of Object.entries(ACTION_COLOR)) {
    if (action?.includes(key)) return val;
  }
  return 'badge-gray';
}

export default function SystemLog() {
  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [filterType,  setFilterType]  = useState('');
  const [expanded,    setExpanded]    = useState(null);
  const [page,        setPage]        = useState(1);
  const PER_PAGE = 50;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 500 });
      if (filterType) params.set('entity_type', filterType);
      const r = await API.get(`/logs?${params}`);
      setLogs(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); setPage(1); }, [filterType]);
  useEffect(() => { setPage(1); }, [search]);


  const filtered = logs.filter(l =>
    !search ||
    l.user_name?.includes(search) ||
    l.action?.includes(search) ||
    l.entity_label?.includes(search) ||
    l.notes?.includes(search)
  );

  const parseVal = (v) => {
    try { return typeof v === 'string' ? JSON.parse(v) : v; }
    catch { return v; }
  };

  const formatVal = (v) => {
    const obj = parseVal(v);
    if (typeof obj === 'object' && obj !== null) {
      return Object.entries(obj).map(([k,val]) =>
        `${k}: ${typeof val === 'object' ? JSON.stringify(val) : val}`
      ).join(' | ');
    }
    return String(v||'');
  };

  const getChangedKeys = (oldV, newV) => {
    try {
      const o = parseVal(oldV); const n = parseVal(newV);
      if (typeof o !== 'object' || typeof n !== 'object') return new Set();
      return new Set(Object.keys({...o,...n}).filter(k => String(o?.[k]||'') !== String(n?.[k]||'')));
    } catch { return new Set(); }
  };

  const renderVal = (v, changed = new Set(), isNew = false) => {
    const obj = parseVal(v);
    if (typeof obj === 'object' && obj !== null) {
      return (
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {Object.entries(obj).map(([k, val]) => {
            const isChanged = changed.has(k);
            return (
              <div key={k} style={{
                display:'flex', gap:6, alignItems:'flex-start',
                background: isChanged ? (isNew ? '#bbf7d0' : '#fecaca') : 'transparent',
                borderRadius:4, padding: isChanged ? '2px 6px' : '2px 0'
              }}>
                <span style={{ fontWeight:700, fontSize:'0.8rem', minWidth:80, color:'#475569' }}>{k}:</span>
                <span style={{ fontSize:'0.82rem', fontWeight: isChanged ? 700 : 400 }}>
                  {typeof val === 'object' ? JSON.stringify(val) : String(val??'')}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return <span style={{ fontSize:'0.82rem' }}>{String(v||'')}</span>;
  };

  const filteredPaged = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">📋 سجل النظام</h1>
        <div style={{ fontSize:'0.85rem', color:'var(--text-muted)' }}>
          {filtered.length} حدث
        </div>
      </div>

      {/* فلاتر */}
      <div className="search-bar" style={{ marginBottom:16 }}>
        <input
          className="search-input"
          placeholder="🔍 بحث بالمستخدم، الإجراء، الطلب..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width:180 }}>
          <option value="">جميع الأنواع</option>
          <option value="order">🛒 الطلبات</option>
          <option value="customer">👥 العملاء</option>
          <option value="payment">💰 الدفعات</option>
          <option value="inventory">📦 المخزون</option>
          <option value="production">🏭 الإنتاج</option>
          <option value="user">👤 المستخدمون</option>
          <option value="product">🏷️ المواد الأولية</option>
          <option value="supplier">🏭 الموردون</option>
        </select>
        <button className="btn btn-secondary" onClick={load}>🔄 تحديث</button>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"/><span>جاري التحميل...</span></div>
      ) : (
        <div className="table-container">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width:140 }}>الوقت</th>
                  <th style={{ width:120 }}>المستخدم</th>
                  <th>الإجراء</th>
                  <th>المرجع</th>
                  <th>التفاصيل</th>
                  <th style={{ width:40 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredPaged.map(l => (
                  <>
                    <tr key={l.id} style={{ cursor: (l.old_value||l.new_value) ? 'pointer' : 'default' }}
                      onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
                      <td style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                        {new Date(l.created_at).toLocaleString('ar-SA', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </td>
                      <td>
                        <div style={{ fontWeight:700, fontSize:'0.85rem' }}>{l.user_name}</div>
                      </td>
                      <td>
                        <span className={`badge ${getActionColor(l.action)}`}>{l.action}</span>
                      </td>
                      <td style={{ fontSize:'0.85rem' }}>
                        <span style={{ marginLeft:4 }}>{ENTITY_ICONS[l.entity_type] || '📌'}</span>
                        {l.entity_label || '-'}
                      </td>
                      <td style={{ fontSize:'0.82rem', color:'var(--text-muted)', maxWidth:260 }}>
                        <span className="truncate" style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {l.notes || '-'}
                        </span>
                      </td>
                      <td style={{ textAlign:'center', color:'var(--text-muted)' }}>
                        {(l.old_value || l.new_value) ? (expanded === l.id ? '▲' : '▼') : ''}
                      </td>
                    </tr>
                    {expanded === l.id && (l.old_value || l.new_value) && (() => {
                      const changed = getChangedKeys(l.old_value, l.new_value);
                      return (
                        <tr key={`exp-${l.id}`}>
                          <td colSpan={6} style={{ background:'#f8f9fb', padding:'12px 20px' }}>
                            {changed.size > 0 && (
                              <div style={{ marginBottom:8, fontSize:'0.78rem', color:'#92400e', background:'#fef3c7', borderRadius:6, padding:'4px 10px', display:'inline-block' }}>
                                ✏️ الحقول المتغيرة: {[...changed].join(' — ')}
                              </div>
                            )}
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                              {l.old_value && (
                                <div style={{ background:'#fff1f2', borderRadius:8, padding:'10px 14px', border:'1px solid #fca5a5' }}>
                                  <div style={{ fontWeight:700, fontSize:'0.78rem', color:'var(--danger)', marginBottom:8 }}>❌ قبل التعديل</div>
                                  {renderVal(l.old_value, changed, false)}
                                </div>
                              )}
                              {l.new_value && (
                                <div style={{ background:'#f0fdf4', borderRadius:8, padding:'10px 14px', border:'1px solid #86efac' }}>
                                  <div style={{ fontWeight:700, fontSize:'0.78rem', color:'var(--success)', marginBottom:8 }}>✅ بعد التعديل</div>
                                  {renderVal(l.new_value, changed, true)}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                  </>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6}>
                    <div className="empty-state"><div className="empty-state-icon">📋</div><p>لا توجد سجلات</p></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          <Pagination total={filtered.length} page={page} perPage={PER_PAGE} onPage={setPage} />
          </div>
        </div>
      )}
    </div>
  );
}
